import React from 'react';
import { ReactQueryProvider } from '@/utils/ReactQuery';
import { BrowserRouter, Route, Routes, useSearchParams } from 'react-router-dom';
import I18n from '@/modules/i18n';
import useGradingStore from '@/components/staffer/Grading/useGradingStore';
import BreadcrumbsNavigator, { BreadcrumbsNavigatorProps } from '@/components/shared/Breadcrumbs/BreadcrumbsNavigator';
import GradingHooks from './GradingHooks';
import CardContainer from './CardContainer';
import CardListSkeleton from './CardListSkeleton';
import GradingSkeleton from './GradingSkeleton';
import HeaderWithFilters from './HeaderWithFilters';
import AssessmentFlashMessage from './AssessmentFlashMessage';
import useIsStudentTransitioning from './useIsStudentTransitioning';

interface GradingProps {
  breadcrumbsData: BreadcrumbsNavigatorProps
  programId: number;
  studentViewAssessmentResults?: boolean;
  userId: number;
  userType: string;
}

const Grading = ({ breadcrumbsData, programId, studentViewAssessmentResults, userId, userType } : GradingProps) => {
  GradingHooks({ programId, studentViewAssessmentResults, userId, userType });
  const { isLoading, assignment, showAnswers, student, submission } = useGradingStore();
  const [searchParams] = useSearchParams();
  const isTransitioningStudent = useIsStudentTransitioning();

  const renderBreadcrumbsNav = () => (
    <BreadcrumbsNavigator
      page="grades"
      searchParams={searchParams}
      {...breadcrumbsData}
    />
  );

  if (isLoading) {
    return (
      <>
        {renderBreadcrumbsNav()}
        <GradingSkeleton />
      </>
    );
  }

  const cardsBody = isTransitioningStudent ? <CardListSkeleton /> : <CardContainer />;

  return (
    <>
      {renderBreadcrumbsNav()}
      <div className="tw-py-4 tw-flex tw-items-center tw-justify-between">
        <h1 className="tw-py-2">
          {assignment.investigation ? I18n.t('investigation_notebook') : assignment.assignment_details.type}
          {' '}
          Results by Student
        </h1>
        {!isTransitioningStudent && (
          <AssessmentFlashMessage
            student={student}
            submission={submission}
          />
        )}
      </div>
      <HeaderWithFilters />
      {showAnswers() ? cardsBody : (
        <div className="tw-text-center tw-text-base tw-m-5">
          Your teacher has disabled viewing answers to assessment questions.
        </div>
      )}
    </>
  );
};

const GradingWrapper = ({
  breadcrumbsData, programId, studentViewAssessmentResults, userId, userType,
} : GradingProps) => (
  <ReactQueryProvider>
    <BrowserRouter basename="/teacher/assignments">
      <Routes>
        <Route
          element={(
            <Grading
              breadcrumbsData={breadcrumbsData}
              programId={programId}
              studentViewAssessmentResults={studentViewAssessmentResults}
              userId={userId}
              userType={userType}
            />
          )}
          path=":assignmentId/students/:studentId/grading"
        />
      </Routes>
    </BrowserRouter>
  </ReactQueryProvider>
);

export default GradingWrapper;
